<template>
  <div v-if="loading">
    <div class="relative text-center mx-auto my-4" style="max-width: 300px;">
      <p class="mb-1 uppercase tracking-widest font-light font-sm opacity-70">Loading Results</p>
    </div>
  </div>
  <template v-else>
    <div class="top_ad"></div>
    <div class="last-updated" v-if="lastUpdated">Last updated: {{ lastUpdated }}</div>
    <div class="page-header container">
      <img src="@/assets/img/ddhq_logo_stacked.png" alt="" class="logo">
      <div>
        <h1>2021 German Federal Elections</h1>
      </div>
      <img src="@/assets/img/europe_elects_logo.png" alt="" class="logo">
    </div>
    <div class="logos-mobile">
      <img src="@/assets/img/ddhq_logo_stacked.png" alt="" class="logo">
      <img src="@/assets/img/europe_elects_logo.png" alt="" class="logo">
    </div>
    <div class="page-description-wrapper">
      <div class="container page-description">
        <div class="headline">After nearly 16 years Angela Merkel is stepping aside as Chancellor of Germany. In the wake of her decision, the 2021 German Federal Elections are the most wide open and unpredictable in a generation.</div>
        <div>We’ll be tracking the votes and seat counts here, while our partners at Europe Elects will be providing commentary and analysis on their <a href="https://twitter.com/EuropeElects" target="_blank" rel="noopener noreferrer">social media</a> and in a live blog on their <a href="https://europeelects.eu/" target="_blank" rel="noopener noreferrer">website</a>.</div>
        <div>Polls close at 6pm in Germany, noon eastern in the United States.</div>
      </div>
    </div>
    <div class="container mx-auto">
      <div class="grid grid-cols-1 gap-12">
        <div>
          <h2>Popular Vote By Party</h2>
          <div class="section-description">German voters get two votes, one to elect a member of parliament from their local constituency (1st Vote) and one for a party that is calculated at the regional level for purposes of proportional representation (2nd vote). The votes do not have to be for the same party. We’re also tracking the change each party receives compared to the 2017 federal election.</div>
          <tabs
            class="mb-6"
            :tabs="tabs"
            :selected="byPartyTab"
            tabKey="byPartyTab">
          </tabs>
          <results-table :topline="topline"></results-table>
        </div>

        <template v-if="$auth.isAuthenticated.value">
          <div>
            <h2>Awarded Seats By Party</h2>
            <div class="section-description mb-6">Seats are awarded by direct vote at constituency level and proportionally at regional level. The German parliament is not fixed in sized so majority to form government is determined after seats are awarded proportionally. Parties need to cross the 5% threshold on the 2nd vote to enter parliament via proportional seats. Designated minority parties do not need to meet the five-percent threshold.</div>
            <seats-chart :topline="topline"></seats-chart>
          </div>

          <div>
            <h2>Projected Seats By Party</h2>
            <div class="section-description mb-6">
              <div class="mb-2">The unique nature of the German election system means the size of the parliament and distribution of seats to each party aren’t officially known until all votes are counted and seats are officially awarded.</div>
              <div>Decision Desk HQ will be providing a projection of the number of seats each party will receive once sufficient partial results are available. The projection will continue to update as more seats are declared.</div>
            </div>
            <seats-chart :topline="topline" :projection="true"></seats-chart>
          </div>

          <div>
            <h2>Results By Constituency</h2>
            <div class="section-description">The maps below track the votes by the 299 individual constituencies. The 1st vote shows the direct election of a member to parliament while the 2nd vote maps shows the support each party is receiving towards proportional representation by constituency.</div>
            <tabs
              class="mb-6"
              :tabs="tabs"
              :selected="mapTab"
              tabKey="mapTab">
            </tabs>
            <results-map
              id="constituency-map"
              geoType="constituencies"
              :results="seats"
            >
            </results-map>
          </div>

          <div class="footer">Results Source: <a href="https://www.bundeswahlleiter.de/en/bundeswahlleiter.html" target="_blank" rel="noopener noreferrer">German Federal Returning Officer</a></div>
        </template>

        <div v-else>
          <div class="login-overlay"></div>
          <login class="login-block shadow-lg rounded-sm"></login>
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import {
  ResultsMap,
  ResultsTable,
  SeatsChart
} from '@/components/charts'
import { mapGetters, mapActions } from 'vuex'

import Login from '@/components/Login'
import Tabs from '@/components/ui/Tabs'

export default {
  name: 'Dashboard',
  components: {
    Login,
    ResultsMap,
    ResultsTable,
    SeatsChart,
    Tabs
  },
  computed: {
    ...mapGetters({
      byPartyTab: 'results/byPartyTab',
      mapTab: 'results/mapTab',
      lastUpdated: 'results/lastUpdated',
      firstSeats: 'results/firstSeats',
      secondSeats: 'results/secondSeats',
      topline: 'results/topline'
    }),
    seats () {
      return this[`${this.mapTab}Seats`]
    }
  },
  methods: {
    ...mapActions({
      getResults: 'results/getResults'
    })
  },
  data: () => ({
    interval: null,
    loading: false,
    tabs: [
      {
        text: 'First Vote',
        value: 'first'
      },
      {
        text: 'Second Vote',
        value: 'second'
      }
    ]
  }),
  async beforeMount () {
    this.loading = true
    if (this.$auth.isAuthenticated.value) {
      await this.getResults()
      this.interval = setInterval(this.getResults, 30 * 1000);
    } else {
      await this.getResults()
    }
    this.loading = false;
  },
  beforeUnmount () {
    clearInterval(this.interval)
  }
};
</script>

<style>
.top_ad {
  display: block;
  height: fit-content;
  width: 100%;
  margin: 10px 0;
  min-height: 100px;
}

.last-updated {
  font-family: 'Roboto Slab', serif;
  font-size: 15px;
  font-weight: 400;
  text-align: center;
  margin-top: 40px;
  color: #222;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px !important;
}

.page-header h1 {
  margin: 0 10px;
}

.logos-mobile {
  display: none;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 30px;
}

.logo {
  height: 60px;
  width: auto;
  padding: 4px 8px;
}

.page-description-wrapper {
  padding: 20px 0;
  background: #fafafa;
  margin-bottom: 40px;
}

.page-description {
  font-family: 'Roboto Slab', serif;
  /* text-align: center; */
  font-size: 15px;
  color: #222;
}

.section-description {
  font-size: 15px;
  color: #222;
  margin-bottom: 15px;
}

.page-description div {
  margin-bottom: 8px;
}

.footer {
  color: #666;
  font-size: 15px;
  padding: 10px 0 30px;
  margin-bottom: 150px;
  border-top: 1px solid #eee;
}

.login-overlay {
  position: fixed;
  height: 100vh;
  width: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 100;
  background: linear-gradient(to bottom, transparent 0%, #222 90%);
}

.login-block {
  position: fixed;
  width: 100%;
  max-width: 400px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  min-height: fit-content;
  background: white;
  padding: 20px;
  margin: 0;
  z-index: 101;
  align-items: center;
}

@media (max-width: 800px) {
  .login-block {
    bottom: 0;
    top: unset;
    transform: translateX(-50%);
  }
}

@media (max-width: 600px) {
  .logos-mobile {
    display: flex;
  }

  .page-header img {
    display: none;
  }
}
</style>
